import { useEffect, useMemo, useState, useCallback } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Beaker,
  CheckCircle2,
  AlertTriangle,
  TrendingDown,
  Loader2,
  PlayCircle,
  RotateCcw,
  Save,
} from "lucide-react";
import { toast } from "sonner";

/**
 * What-if Pricing Simulator (admin-only).
 *
 * Lets the admin temporarily edit slabs / zone multipliers / surcharge rates /
 * weight caps / op-cost assumptions and see margin impact without saving.
 * "Apply to Live Pricing" persists via PUT /api/admin/pricing-config.
 */

const STATUS_PILL = {
  ok:   { bg: "bg-emerald-50", fg: "text-emerald-700", border: "border-emerald-200", label: "Healthy", icon: CheckCircle2 },
  thin: { bg: "bg-amber-50",   fg: "text-amber-700",   border: "border-amber-200",   label: "Thin",    icon: AlertTriangle },
  loss: { bg: "bg-rose-50",    fg: "text-rose-700",    border: "border-rose-200",    label: "Loss",    icon: TrendingDown },
};

function StatusPill({ status }) {
  const cfg = STATUS_PILL[status] || STATUS_PILL.ok;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${cfg.bg} ${cfg.fg} ${cfg.border}`}
      data-testid={`sim-status-${status}`}
    >
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

const DEFAULT_OP = {
  pickup_agent: 27,
  last_mile_agent: 25,
  hub1_handling: 15,
  hub2_handling: 15,
  packing_material: 15,
  insurance_reserve: 5,
  linehaul_per_kg_km: 0.10,
};

const DEFAULT_LANE = { weight_kg: 5, distance_km: 1200, prepaid: true };

export default function PricingSimulator() {
  const [liveConfig, setLiveConfig] = useState(null);
  const [draft, setDraft] = useState(null);
  const [opOverrides, setOpOverrides] = useState(DEFAULT_OP);
  const [lane, setLane] = useState(DEFAULT_LANE);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ─── Load live config on mount ─────────────────────────────────
  const loadLive = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/pricing-config");
      setLiveConfig(data);
      setDraft(JSON.parse(JSON.stringify(data)));
    } catch (err) {
      toast.error("Failed to load live pricing config");
    }
  }, []);

  useEffect(() => {
    loadLive();
  }, [loadLive]);

  // ─── Run simulation (debounced) ────────────────────────────────
  const runSimulation = useCallback(async (silent = false) => {
    if (!draft) return;
    if (!silent) setRunning(true);
    try {
      const { data } = await api.post("/admin/pricing-health/simulate", {
        pricing_config: {
          weight_slabs: draft.weight_slabs,
          zone_multipliers: draft.zone_multipliers,
          gst_rate: draft.gst_rate,
          heavy_overflow_per_kg: draft.heavy_overflow_per_kg,
          longhaul_surcharge_free_kg: draft.longhaul_surcharge_free_kg,
          longhaul_surcharge_per_kgkm: draft.longhaul_surcharge_per_kgkm,
          zone_max_weight_kg: draft.zone_max_weight_kg,
        },
        op_cost_overrides: Object.fromEntries(
          Object.entries(opOverrides).map(([k, v]) => [k, Number(v)])
        ),
        lane: {
          weight_kg: Number(lane.weight_kg) || 0,
          distance_km: Number(lane.distance_km) || 0,
          prepaid: !!lane.prepaid,
        },
        prepaid: !!lane.prepaid,
      });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Simulation failed");
    } finally {
      setRunning(false);
    }
  }, [draft, opOverrides, lane]);

  // Debounced auto-run on any input change
  useEffect(() => {
    if (!draft) return;
    const t = setTimeout(() => runSimulation(true), 350);
    return () => clearTimeout(t);
  }, [draft, opOverrides, lane, runSimulation]);

  // ─── Reset to live ─────────────────────────────────────────────
  const reset = () => {
    if (!liveConfig) return;
    setDraft(JSON.parse(JSON.stringify(liveConfig)));
    setOpOverrides(DEFAULT_OP);
    setLane(DEFAULT_LANE);
    toast.success("Reset to live pricing");
  };

  // ─── Apply (persist) ───────────────────────────────────────────
  const apply = async () => {
    if (!draft) return;
    setApplying(true);
    try {
      await api.put("/admin/pricing-config", {
        weight_slabs: draft.weight_slabs,
        zone_multipliers: draft.zone_multipliers,
        gst_rate: Number(draft.gst_rate) || 0.18,
        heavy_overflow_per_kg: Number(draft.heavy_overflow_per_kg) || 30,
        longhaul_surcharge_free_kg: Number(draft.longhaul_surcharge_free_kg) || 0,
        longhaul_surcharge_per_kgkm: Object.fromEntries(
          Object.entries(draft.longhaul_surcharge_per_kgkm || {}).map(([z, v]) => [z, Number(v) || 0])
        ),
        zone_max_weight_kg: Object.fromEntries(
          Object.entries(draft.zone_max_weight_kg || {}).map(([z, v]) => [z, Number(v) || 0])
        ),
      });
      toast.success("Applied to live pricing — customers see this now.");
      await loadLive();
      setConfirmOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply");
    } finally {
      setApplying(false);
    }
  };

  // ─── Has any change vs live? ───────────────────────────────────
  const isDirty = useMemo(() => {
    if (!draft || !liveConfig) return false;
    return JSON.stringify(draft) !== JSON.stringify(liveConfig);
  }, [draft, liveConfig]);

  if (!draft) {
    return (
      <div className="flex items-center justify-center py-10 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading simulator …
      </div>
    );
  }

  const updateSlab = (idx, field, value) => {
    const next = JSON.parse(JSON.stringify(draft));
    next.weight_slabs[idx][field] = field === "label" ? value : Number(value) || 0;
    setDraft(next);
  };
  const updateZone = (zid, field, value) => {
    const next = JSON.parse(JSON.stringify(draft));
    next.zone_multipliers[zid][field] = field === "label" ? value : Number(value) || 0;
    setDraft(next);
  };
  const updateSurcharge = (zid, value) => {
    const next = JSON.parse(JSON.stringify(draft));
    if (!next.longhaul_surcharge_per_kgkm) next.longhaul_surcharge_per_kgkm = {};
    next.longhaul_surcharge_per_kgkm[zid] = Number(value) || 0;
    setDraft(next);
  };
  const updateCap = (zid, value) => {
    const next = JSON.parse(JSON.stringify(draft));
    if (!next.zone_max_weight_kg) next.zone_max_weight_kg = {};
    next.zone_max_weight_kg[zid] = Number(value) || 0;
    setDraft(next);
  };

  const single = result?.single_lane;
  const summary = result?.summary || { ok: 0, thin: 0, loss: 0 };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-5" data-testid="pricing-simulator">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
            <Beaker className="h-5 w-5 text-violet-600" />
          </div>
          <div>
            <h3 className="font-['Outfit'] font-bold text-slate-900">What-if Pricing Simulator</h3>
            <p className="text-xs text-slate-500">Edit values below to preview margin impact. Nothing is saved until you click Apply.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => runSimulation(false)}
            disabled={running}
            className="rounded-xl h-9 gap-1.5"
            data-testid="sim-run-btn"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
            Run Simulation
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={reset}
            disabled={!isDirty || applying}
            className="rounded-xl h-9 gap-1.5"
            data-testid="sim-reset-btn"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to Live
          </Button>
          <Button
            size="sm"
            onClick={() => setConfirmOpen(true)}
            disabled={!isDirty || applying}
            className="bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl h-9 gap-1.5 disabled:opacity-50"
            data-testid="sim-apply-btn"
          >
            <Save className="h-4 w-4" />
            Apply to Live
          </Button>
        </div>
      </div>

      {/* Dirty banner */}
      {isDirty && (
        <div className="rounded-xl border border-violet-200 bg-violet-50 p-3 text-xs text-violet-800" data-testid="sim-dirty-banner">
          You have unsaved changes. The lane table below shows the simulated outcome — customers still see the live pricing until you click <strong>Apply</strong>.
        </div>
      )}

      {/* ── Editable inputs ─────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Slabs */}
        <div className="rounded-xl border border-slate-100 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Weight slabs (₹)</p>
          <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
            {draft.weight_slabs?.map((s, idx) => (
              <div key={idx} className="grid grid-cols-2 gap-2 items-center text-xs">
                <span className="text-slate-600 truncate">{s.label}</span>
                <Input
                  type="number"
                  value={s.charge ?? 0}
                  onChange={(e) => updateSlab(idx, "charge", e.target.value)}
                  className="h-8 rounded-lg text-xs"
                  data-testid={`sim-slab-${idx}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Zones */}
        <div className="rounded-xl border border-slate-100 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Zone multipliers · surcharges · caps</p>
          <div className="space-y-2">
            {Object.entries(draft.zone_multipliers || {}).map(([zid, z]) => (
              <div key={zid} className="grid grid-cols-[auto_1fr_1fr_1fr] gap-2 items-center text-xs">
                <span className="font-bold text-slate-700 w-8">{zid}</span>
                <div>
                  <Label className="text-[9px] uppercase text-slate-400">×</Label>
                  <Input
                    type="number"
                    step="0.05"
                    value={z.multiplier ?? 1}
                    onChange={(e) => updateZone(zid, "multiplier", e.target.value)}
                    className="h-8 rounded-lg text-xs"
                    data-testid={`sim-zone-${zid}-mult`}
                  />
                </div>
                <div>
                  <Label className="text-[9px] uppercase text-amber-600">₹/kg/km</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={draft.longhaul_surcharge_per_kgkm?.[zid] ?? 0}
                    onChange={(e) => updateSurcharge(zid, e.target.value)}
                    className="h-8 rounded-lg text-xs"
                    data-testid={`sim-zone-${zid}-surcharge`}
                  />
                </div>
                <div>
                  <Label className="text-[9px] uppercase text-rose-600">cap kg</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={draft.zone_max_weight_kg?.[zid] ?? 0}
                    onChange={(e) => updateCap(zid, e.target.value)}
                    className="h-8 rounded-lg text-xs"
                    data-testid={`sim-zone-${zid}-cap`}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Free kg</Label>
              <Input
                type="number"
                step="0.5"
                value={draft.longhaul_surcharge_free_kg ?? 0}
                onChange={(e) => setDraft({ ...draft, longhaul_surcharge_free_kg: Number(e.target.value) || 0 })}
                className="h-8 rounded-lg text-xs"
                data-testid="sim-free-kg"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">GST %</Label>
              <Input
                type="number"
                step="1"
                value={Math.round((draft.gst_rate ?? 0.18) * 100)}
                onChange={(e) => setDraft({ ...draft, gst_rate: (Number(e.target.value) || 0) / 100 })}
                className="h-8 rounded-lg text-xs"
                data-testid="sim-gst"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Overflow ₹/kg</Label>
              <Input
                type="number"
                value={draft.heavy_overflow_per_kg ?? 30}
                onChange={(e) => setDraft({ ...draft, heavy_overflow_per_kg: Number(e.target.value) || 0 })}
                className="h-8 rounded-lg text-xs"
                data-testid="sim-overflow"
              />
            </div>
          </div>
        </div>

        {/* Op cost */}
        <div className="rounded-xl border border-slate-100 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Op-cost assumptions</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {[
              ["pickup_agent",       "Pickup agent ₹"],
              ["last_mile_agent",    "Last-mile ₹"],
              ["hub1_handling",      "Hub-1 ₹"],
              ["hub2_handling",      "Hub-2 ₹"],
              ["packing_material",   "Packing ₹"],
              ["insurance_reserve",  "Insurance ₹"],
              ["linehaul_per_kg_km", "Linehaul ₹/kg/km"],
            ].map(([key, label]) => (
              <div key={key}>
                <Label className="text-[10px] uppercase text-slate-500">{label}</Label>
                <Input
                  type="number"
                  step={key === "linehaul_per_kg_km" ? "0.01" : "1"}
                  value={opOverrides[key] ?? 0}
                  onChange={(e) => setOpOverrides({ ...opOverrides, [key]: Number(e.target.value) || 0 })}
                  className="h-8 rounded-lg text-xs"
                  data-testid={`sim-op-${key}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Custom lane */}
        <div className="rounded-xl border border-slate-100 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Custom lane test</p>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Weight (kg)</Label>
              <Input
                type="number"
                step="0.5"
                value={lane.weight_kg}
                onChange={(e) => setLane({ ...lane, weight_kg: e.target.value })}
                className="h-9 rounded-lg text-xs"
                data-testid="sim-lane-weight"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Distance (km)</Label>
              <Input
                type="number"
                step="1"
                value={lane.distance_km}
                onChange={(e) => setLane({ ...lane, distance_km: e.target.value })}
                className="h-9 rounded-lg text-xs"
                data-testid="sim-lane-distance"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Payment</Label>
              <select
                className="h-9 w-full rounded-lg border border-slate-200 px-2 text-xs bg-white"
                value={lane.prepaid ? "prepaid" : "cod"}
                onChange={(e) => setLane({ ...lane, prepaid: e.target.value === "prepaid" })}
                data-testid="sim-lane-payment"
              >
                <option value="prepaid">Prepaid</option>
                <option value="cod">COD</option>
              </select>
            </div>
          </div>

          {single && (
            <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 p-3 space-y-1 text-xs" data-testid="sim-single-result">
              <div className="flex justify-between text-slate-600">
                <span>Customer service charges</span>
                <span className="font-mono">₹{single.customer_service_charges}</span>
              </div>
              <div className="flex justify-between text-slate-500">
                <span>GST</span>
                <span className="font-mono">₹{single.customer_gst}</span>
              </div>
              <div className="flex justify-between font-bold text-slate-900 border-t border-slate-200 pt-1">
                <span>Total payable</span>
                <span className="font-mono">₹{single.customer_total}</span>
              </div>
              <div className="flex justify-between text-slate-500 mt-1">
                <span>Op cost (median)</span>
                <span className="font-mono">−₹{single.op_cost_median}</span>
              </div>
              {single.razorpay_fee_estimate > 0 && (
                <div className="flex justify-between text-slate-400">
                  <span>Razorpay (2%)</span>
                  <span className="font-mono">−₹{single.razorpay_fee_estimate}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-slate-900 border-t border-slate-200 pt-1">
                <span>Net margin (median)</span>
                <span className={`font-mono ${single.margin_median >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                  ₹{single.margin_median} <span className="text-[10px] text-slate-400">({single.margin_pct_median}%)</span>
                </span>
              </div>
              <div className="flex items-center justify-between pt-1">
                <span className="text-[10px] text-slate-500">
                  Best ₹{single.margin_best} · Worst ₹{single.margin_worst}
                </span>
                <StatusPill status={single.status} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Standard lanes (simulated) ────────────────────── */}
      <div className="rounded-xl border border-slate-100 p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Standard lanes (simulated)</p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {summary.ok} ok · {summary.thin} thin · {summary.loss} loss
            </p>
          </div>
          {running && <span className="text-[11px] text-slate-400 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Recomputing…</span>}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase text-slate-500 border-b border-slate-100">
                <th className="pb-2 font-semibold">Lane</th>
                <th className="pb-2 font-semibold text-right">Cust svc</th>
                <th className="pb-2 font-semibold text-right">Op (med)</th>
                <th className="pb-2 font-semibold text-right">Margin</th>
                <th className="pb-2 font-semibold text-right">GM%</th>
                <th className="pb-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {(result?.lanes || []).map((l, idx) => (
                <tr key={l.label} className="border-b border-slate-50" data-testid={`sim-lane-${idx}`}>
                  <td className="py-1.5 pr-2 text-slate-800">{l.label}</td>
                  <td className="py-1.5 text-right font-mono">₹{l.customer_service_charges}</td>
                  <td className="py-1.5 text-right font-mono text-slate-500">₹{l.op_cost_median}</td>
                  <td className={`py-1.5 text-right font-mono font-bold ${l.margin_median >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    ₹{l.margin_median}
                  </td>
                  <td className="py-1.5 text-right font-mono text-slate-500">{l.margin_pct_median}%</td>
                  <td className="py-1.5"><StatusPill status={l.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirmation modal */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="sim-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Apply pricing changes to live customers?</AlertDialogTitle>
            <AlertDialogDescription>
              This replaces the live pricing config. New orders placed from this moment will use these slabs / multipliers / surcharges. Existing orders are not affected.
              {summary.loss > 0 && (
                <span className="block mt-2 text-rose-700 font-semibold">
                  ⚠ Warning: {summary.loss} standard lane(s) are loss-making in worst-case under this configuration.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={applying} data-testid="sim-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={apply}
              disabled={applying}
              className="bg-[#FF6B00] hover:bg-[#E65A00]"
              data-testid="sim-confirm-apply"
            >
              {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Yes, apply now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
